import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { CSSProperties, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLatest, useLocalStorage, useMedia } from "react-use";

import { getServerUrl } from "config/backend";
import { getExplorerUrl } from "config/chains";
import { TV_SAVE_LOAD_CHARTS_KEY } from "config/localStorage";
import { SUPPORTED_RESOLUTIONS_V1, SUPPORTED_RESOLUTIONS_V2 } from "config/tradingview";
import { useSettings } from "context/SettingsContext/SettingsContextProvider";
import { useSyntheticsEvents } from "context/SyntheticsEvents/SyntheticsEventsProvider";
import { selectPositionsInfoData } from "context/SyntheticsStateContext/selectors/globalSelectors";
import { makeSelectMarketPriceDecimals } from "context/SyntheticsStateContext/selectors/statsSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { shouldInvertChartPrice } from "domain/prices/chartInversion";
import { TokenPrices } from "domain/tokens";
import { DataFeed, SyntheticTradeMark, TradeMarkDetails } from "domain/tradingview/DataFeed";
import {
  formatTradeMarkPrice,
  getExecutedTradeMarkSide,
  getTradeMarkExecutionType,
  getTradeMarkOrderType,
  getTradeMarkPriceLabel,
} from "domain/tradingview/tradeMarks";
import { getObjectKeyFromValue, getSymbolName } from "domain/tradingview/utils";
import { formatDateTime } from "lib/dates";
import { helperToast } from "lib/helperToast";
import { getTradingViewLocale } from "lib/i18n";
import { shortenAddress } from "lib/legacy";
import { formatUsd } from "lib/numbers";
import { useTradePageVersion } from "lib/useTradePageVersion";
import useWallet from "lib/wallets/useWallet";
import { isChartAvailableForToken } from "sdk/configs/tokens";
import { isCngnToken } from "sdk/utils/cngnCurrency";

import Loader from "components/Common/Loader";
import ExternalLink from "components/ExternalLink/ExternalLink";
import Modal from "components/Modal/Modal";

import { defaultChartProps, disabledFeaturesOnMobile } from "./constants";
import { DynamicLines } from "./DynamicLines";
import { SaveLoadAdapter } from "./SaveLoadAdapter";
import { StaticLines } from "./StaticLines";
import type { StaticChartLine } from "./types";
import type {
  ChartData,
  ChartingLibraryWidgetOptions,
  IChartingLibraryWidget,
  IChartWidgetApi,
  ResolutionString,
} from "../../charting_library";

const CHART_IMAGE_MIME = "image/png";
const TRADE_MARKS_REFRESH_INTERVAL_MS = 5_000;
const RESET_VISIBLE_RANGE_SECONDS_BY_RESOLUTION: Record<string, number> = {
  "1": 24 * 60 * 60,
  "5": 3 * 24 * 60 * 60,
  "15": 7 * 24 * 60 * 60,
  "60": 30 * 24 * 60 * 60,
  "240": 90 * 24 * 60 * 60,
  "1D": 365 * 24 * 60 * 60,
  "1W": 5 * 365 * 24 * 60 * 60,
  "1M": 10 * 365 * 24 * 60 * 60,
};

function getResetVisibleRange(interval?: string) {
  if (!interval) return undefined;

  const visibleRangeSeconds = RESET_VISIBLE_RANGE_SECONDS_BY_RESOLUTION[interval];
  if (!visibleRangeSeconds) return undefined;

  const to = Math.floor(Date.now() / 1000);
  return {
    from: to - visibleRangeSeconds,
    to,
  };
}

function resetChartData(
  chart: IChartWidgetApi | undefined,
  datafeed: DataFeed | null | undefined,
  symbol: string | undefined,
  interval: ResolutionString | undefined
) {
  if (!chart || !symbol || !interval) return;

  datafeed?.resetBars(symbol, interval);

  const visibleRange = getResetVisibleRange(interval);
  if (visibleRange) {
    chart
      .setVisibleRange(visibleRange)
      .catch(() => undefined)
      .finally(() => {
        chart.resetData();
      });
    return;
  }

  chart.resetData();
}

function getChartSnapshotFilename(symbol: string) {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");

  return `${symbol.toLowerCase()}-chart-${year}-${month}-${day}-${hours}-${minutes}-${seconds}.png`;
}

type Props = {
  chainId: number;
  chartLines: StaticChartLine[];
  savedShouldShowPositionLines?: boolean;
  showDynamicChartLines?: boolean;
  period: string;
  setPeriod: (period: string) => void;
  chartToken:
    | ({
        symbol: string;
      } & TokenPrices)
    | { symbol: string };
  marketAddress?: string;
  supportedResolutions: typeof SUPPORTED_RESOLUTIONS_V1 | typeof SUPPORTED_RESOLUTIONS_V2;
  visualMultiplier?: number;
  setIsCandlesLoaded?: (isCandlesLoaded: boolean) => void;
};

function getOrderTypeLabel(orderType: number | undefined) {
  // Order type is the original order kind shown to the user.
  // Increase/decrease direction is handled separately via executionType.
  // That is why both increase and decrease variants collapse to the same label here:
  // 2/4 are market orders, 3/5 are limit orders.
  switch (orderType) {
    case 0:
      return "Market Swap";
    case 1:
      return "Limit Swap";
    case 2:
      return "Market";
    case 3:
      return "Limit";
    case 4:
      return "Market";
    case 5:
      return "Limit";
    case 6:
      return "Stop Loss";
    case 7:
      return "Liquidation";
    case 8:
      return "Stop";
    default:
      return "-";
  }
}

function getExecutionTypeLabel(executionType: number | undefined) {
  // Execution type normalizes executed orders into the action that happened on fill.
  switch (executionType) {
    case 0:
      return "Market Swap";
    case 2:
      return "Market Increase";
    case 4:
      return "Market Decrease";
    case 7:
      return "Liquidation";
    default:
      return "-";
  }
}

function toBigIntOrUndefined(value: string | undefined) {
  if (!value) return undefined;
  try {
    return BigInt(value);
  } catch (_error) {
    return undefined;
  }
}

export default function TVChartContainer({
  chartToken,
  marketAddress,
  chainId,
  chartLines,
  savedShouldShowPositionLines,
  showDynamicChartLines,
  period,
  setPeriod,
  supportedResolutions,
  visualMultiplier,
  setIsCandlesLoaded,
}: Props) {
  const { i18n } = useLingui();
  const chartLocale = getTradingViewLocale(i18n.locale);
  const { shouldShowBSPositionLines, shouldShowPositionLines } = useSettings();
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const tvWidgetRef = useRef<IChartingLibraryWidget | null>(null);
  const latestPriceLineColorRef = useRef<string>();
  const [chartReady, setChartReady] = useState(false);
  const [isChartChangingSymbol, setIsChartChangingSymbol] = useState(false);
  const [chartDataLoading, setChartDataLoading] = useState(true);
  const [tvCharts, setTvCharts] = useLocalStorage<ChartData[] | undefined>(TV_SAVE_LOAD_CHARTS_KEY, []);
  const [selectedTradeMark, setSelectedTradeMark] = useState<TradeMarkDetails | undefined>();
  const explorerUrl = useMemo(() => getExplorerUrl(chainId), [chainId]);

  const [tradePageVersion] = useTradePageVersion();
  const { account } = useWallet();
  const positionsInfoData = useSelector(selectPositionsInfoData);
  const { positionIncreaseEvents, positionDecreaseEvents } = useSyntheticsEvents();
  const chartTokenAddress =
    "address" in chartToken && typeof chartToken.address === "string" ? chartToken.address : undefined;
  const marketPriceDecimals = useSelector(makeSelectMarketPriceDecimals(chartTokenAddress));

  const [datafeed, setDatafeed] = useState<DataFeed | null>(null);
  const syntheticTradeMarks = useMemo<SyntheticTradeMark[]>(() => {
    if (!account || !marketAddress) {
      return [];
    }

    const normalizedAccount = account.toLowerCase();
    const normalizedMarketAddress = marketAddress.toLowerCase();
    const increaseEventByPositionKey = new Map(
      (positionIncreaseEvents || [])
        .filter((event) => event.marketAddress?.toLowerCase() === normalizedMarketAddress && event.increasedAtTime > 0n)
        .map((event) => [event.positionKey, event] as const)
    );
    const openPositionMarks = Object.values(positionsInfoData || {}).flatMap((position) => {
      if (position.marketAddress?.toLowerCase() !== normalizedMarketAddress || position.increasedAtTime <= 0n) {
        return [];
      }

      const entryPrice = position.entryPrice;
      if (typeof entryPrice !== "bigint" || entryPrice <= 0n) {
        return [];
      }

      const side: "B" | "S" = position.isLong ? "B" : "S";
      const sourceIncreaseEvent = increaseEventByPositionKey.get(position.key);

      return [
        {
          id: `open-position:${position.key}`,
          account: normalizedAccount,
          marketAddress: normalizedMarketAddress,
          side,
          source: "open-position" as const,
          isLong: position.isLong,
          timestamp: Number(position.increasedAtTime),
          executionPrice: entryPrice.toString(),
          sizeDeltaUsd: position.sizeInUsd.toString(),
          orderType: sourceIncreaseEvent?.orderType,
          executionType: sourceIncreaseEvent ? 2 : undefined,
        },
      ];
    });

    const executedIncreaseMarks = (positionIncreaseEvents || [])
      .filter((event) => event.marketAddress?.toLowerCase() === normalizedMarketAddress && event.increasedAtTime > 0n)
      .flatMap((event) => {
        const side = getExecutedTradeMarkSide(event.orderType, event.isLong);
        if (!side) {
          return [];
        }

        return [
          {
            id: `position-increase:${event.orderKey}:${event.increasedAtTime.toString()}`,
            account: normalizedAccount,
            orderKey: event.orderKey,
            marketAddress: normalizedMarketAddress,
            side,
            source: "executed" as const,
            isLong: event.isLong,
            orderType: event.orderType,
            timestamp: Number(event.increasedAtTime),
            executionPrice: event.executionPrice.toString(),
            sizeDeltaUsd: event.sizeDeltaUsd.toString(),
          },
        ];
      });

    const executedDecreaseMarks = (positionDecreaseEvents || [])
      .filter((event) => event.marketAddress?.toLowerCase() === normalizedMarketAddress && event.decreasedAtTime > 0n)
      .flatMap((event) => {
        const side = getExecutedTradeMarkSide(event.orderType, event.isLong);
        if (!side) {
          return [];
        }

        return [
          {
            id: `position-decrease:${event.orderKey}:${event.decreasedAtTime.toString()}`,
            account: normalizedAccount,
            orderKey: event.orderKey,
            marketAddress: normalizedMarketAddress,
            side,
            source: "executed" as const,
            isLong: event.isLong,
            orderType: event.orderType,
            timestamp: Number(event.decreasedAtTime),
            sizeDeltaUsd: event.sizeDeltaUsd.toString(),
          },
        ];
      });

    return [...executedIncreaseMarks, ...executedDecreaseMarks, ...openPositionMarks];
  }, [account, marketAddress, positionDecreaseEvents, positionIncreaseEvents, positionsInfoData]);

  const selectedTradeMarkPrice = useMemo(
    () =>
      formatTradeMarkPrice(selectedTradeMark, {
        symbol: chartToken.symbol,
        displayDecimals: marketPriceDecimals,
        visualMultiplier,
      }),
    [chartToken.symbol, marketPriceDecimals, selectedTradeMark, visualMultiplier]
  );

  const blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject(new Error("Failed to read chart image"));
        }
      };
      reader.onerror = () => reject(new Error("Failed to read chart image"));
      reader.readAsDataURL(blob);
    });

  const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
    new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create chart image blob"));
        }
      }, CHART_IMAGE_MIME);
    });

  const getChartSnapshot = async () => {
    if (!tvWidgetRef.current) {
      throw new Error("Chart is not ready");
    }

    const canvas = await tvWidgetRef.current.takeClientScreenshot();
    const blob = await canvasToBlob(canvas);
    const dataUrl = await blobToDataUrl(blob);
    return { blob, dataUrl };
  };

  const downloadChartSnapshot = async () => {
    const { blob, dataUrl } = await getChartSnapshot();
    const url = URL.createObjectURL(blob);
    try {
      const link = document.createElement("a");
      link.href = url;
      link.download = getChartSnapshotFilename(chartToken.symbol);
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (_error) {
      // Some browsers/webviews block programmatic download. Open image as fallback.
      window.open(dataUrl, "_blank", "noopener,noreferrer");
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  const copyChartSnapshot = async () => {
    const { blob, dataUrl } = await getChartSnapshot();

    if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
      return "image";
    }

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(dataUrl);
      return "data-url";
    }

    throw new Error("Clipboard is not supported");
  };

  useEffect(() => {
    const oracleBaseUrl = getServerUrl(chainId, "oracle-keeper");
    const newDatafeed = new DataFeed(chainId, oracleBaseUrl, tradePageVersion);
    newDatafeed.addEventListener("candlesDisplay.success", (event: Event) => {
      const isFirstDraw = (event as CustomEvent).detail.isFirstTimeLoad;
      if (isFirstDraw) {
        if (setIsCandlesLoaded) {
          setIsCandlesLoaded(true);
        }
      }

      setChartDataLoading(false);
    });
    setDatafeed((prev) => {
      if (prev) {
        prev.destroy();
      }
      return newDatafeed;
    });
  }, [chainId, setIsCandlesLoaded, tradePageVersion]);

  useEffect(() => {
    if (shouldShowBSPositionLines) {
      datafeed?.setMarksContext(account, marketAddress);
    } else {
      datafeed?.setMarksContext(undefined, undefined);
    }

    if (!chartReady || !tvWidgetRef.current) {
      return;
    }

    const chart = tvWidgetRef.current.activeChart();
    chart?.clearMarks?.();

    if (shouldShowBSPositionLines) {
      chart?.refreshMarks?.();
    }
  }, [account, chartReady, datafeed, marketAddress, shouldShowBSPositionLines]);

  useEffect(() => {
    if (!chartReady || !tvWidgetRef.current || !shouldShowBSPositionLines || !account || !marketAddress) {
      return;
    }

    const intervalId = window.setInterval(() => {
      tvWidgetRef.current?.activeChart()?.refreshMarks?.();
    }, TRADE_MARKS_REFRESH_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [account, chartReady, marketAddress, shouldShowBSPositionLines]);

  useEffect(() => {
    datafeed?.setSyntheticTradeMarks(syntheticTradeMarks);

    if (!chartReady || !tvWidgetRef.current || !shouldShowBSPositionLines || !account || !marketAddress) {
      return;
    }

    tvWidgetRef.current.activeChart().refreshMarks?.();
  }, [account, chartReady, datafeed, marketAddress, shouldShowBSPositionLines, syntheticTradeMarks]);

  useEffect(() => {
    setSelectedTradeMark(undefined);
  }, [chartToken.symbol, marketAddress, account, chainId, shouldShowBSPositionLines]);

  useEffect(() => {
    if (!datafeed) return;

    const handlePriceLineColorChange = (event: Event) => {
      const color = (event as CustomEvent<{ color?: string }>).detail?.color;
      if (!color) return;

      latestPriceLineColorRef.current = color;
      if (!tvWidgetRef.current) return;

      tvWidgetRef.current.applyOverrides({
        "mainSeriesProperties.priceLineColor": color,
      });
    };

    datafeed.addEventListener("priceLineColor.change", handlePriceLineColorChange);

    return () => {
      datafeed.removeEventListener("priceLineColor.change", handlePriceLineColorChange);
    };
  }, [datafeed]);

  useEffect(() => {
    if (!chartReady || !tvWidgetRef.current || !latestPriceLineColorRef.current) return;

    tvWidgetRef.current.applyOverrides({
      "mainSeriesProperties.priceLineColor": latestPriceLineColorRef.current,
    });
  }, [chartReady]);

  const isMobile = useMedia("(max-width: 550px)");
  const symbolRef = useRef(chartToken.symbol);
  symbolRef.current = chartToken.symbol;
  const chartTokenAvgPrice =
    (chartToken as TokenPrices | undefined)?.minPrice !== undefined &&
    (chartToken as TokenPrices | undefined)?.maxPrice !== undefined
      ? ((chartToken as TokenPrices).minPrice + (chartToken as TokenPrices).maxPrice) / 2n
      : undefined;
  const shouldInvert = isCngnToken(chartToken.symbol) || shouldInvertChartPrice(chartTokenAvgPrice);

  useEffect(() => {
    if (
      !chartReady ||
      !tvWidgetRef.current ||
      !chartToken.symbol ||
      !isChartAvailableForToken(chainId, chartToken.symbol)
    ) {
      return;
    }

    const newSymbolWithMultiplier = getSymbolName(chartToken.symbol, visualMultiplier, shouldInvert);
    const currentSymbolInfo = tvWidgetRef.current?.activeChart().symbolExt();
    const currentSymbolWithMultiplier = currentSymbolInfo
      ? (() => {
          const unitId = currentSymbolInfo.unit_id;
          const isInverted = unitId?.startsWith("i") ?? false;
          const rawMultiplier = unitId ? (isInverted ? unitId.slice(1) : unitId) : undefined;
          const parsedMultiplier = rawMultiplier ? parseInt(rawMultiplier) : undefined;
          return getSymbolName(currentSymbolInfo.name, parsedMultiplier, isInverted);
        })()
      : undefined;

    if (newSymbolWithMultiplier !== currentSymbolWithMultiplier) {
      setIsChartChangingSymbol(true);
      setChartDataLoading(true);

      tvWidgetRef.current.setSymbol(newSymbolWithMultiplier, tvWidgetRef.current.activeChart().resolution(), () => {
        const activeChart = tvWidgetRef.current?.activeChart();
        resetChartData(activeChart, datafeed, chartToken.symbol, activeChart?.resolution());

        const priceScale = activeChart?.getPanes().at(0)?.getMainSourcePriceScale();
        if (priceScale) {
          priceScale.setAutoScale(true);
        }
        setIsChartChangingSymbol(false);
      });
    }
  }, [chainId, chartReady, chartToken.symbol, datafeed, visualMultiplier, shouldInvert]);

  const lastPeriod = useLatest(period);
  const lastSupportedResolutions = useLatest(supportedResolutions);

  useLayoutEffect(() => {
    if (chartToken.symbol) {
      datafeed?.prefetchBars(
        chartToken.symbol,
        getObjectKeyFromValue(lastPeriod.current, lastSupportedResolutions.current) as ResolutionString
      );
    }
  }, [chartToken.symbol, datafeed, lastPeriod, lastSupportedResolutions]);

  useEffect(() => {
    if (!datafeed) return;

    const widgetOptions: ChartingLibraryWidgetOptions = {
      debug: false,
      symbol: symbolRef.current && getSymbolName(symbolRef.current, visualMultiplier, shouldInvert), // Using ref to avoid unnecessary re-renders on symbol change and still have access to the latest symbol
      datafeed,
      theme: defaultChartProps.theme,
      container: chartContainerRef.current!,
      library_path: defaultChartProps.library_path,
      locale: chartLocale,
      loading_screen: defaultChartProps.loading_screen,
      enabled_features: defaultChartProps.enabled_features,
      disabled_features: isMobile
        ? defaultChartProps.disabled_features.concat(disabledFeaturesOnMobile)
        : defaultChartProps.disabled_features,
      client_id: defaultChartProps.client_id,
      user_id: defaultChartProps.user_id,
      fullscreen: defaultChartProps.fullscreen,
      autosize: defaultChartProps.autosize,
      custom_css_url: defaultChartProps.custom_css_url,
      overrides: defaultChartProps.overrides,
      custom_translate_function: defaultChartProps.custom_translate_function,
      interval: getObjectKeyFromValue(period, supportedResolutions) as ResolutionString,
      favorites: { ...defaultChartProps.favorites, intervals: Object.keys(supportedResolutions) as ResolutionString[] },
      custom_formatters: defaultChartProps.custom_formatters,
      load_last_chart: false,
      auto_save_delay: 1,
      time_scale: {
        min_bar_spacing: 0.5,
      },
      time_frames: [
        { text: "24h", resolution: "1" as ResolutionString, description: t`24 Hours`, title: "24h" },
        { text: "7d", resolution: "60" as ResolutionString, description: t`7 Days`, title: "7d" },
        { text: "1m", resolution: "D" as ResolutionString, description: t`1 Month`, title: "1m" },
      ],
      // save_load_adapter: new SaveLoadAdapter(tvCharts, setTvCharts, tradePageVersion),
      save_load_adapter: new SaveLoadAdapter(tvCharts, setTvCharts, tradePageVersion),
    };
    tvWidgetRef.current = new window.TradingView.widget(widgetOptions);

    tvWidgetRef.current!.onChartReady(function () {
      setChartReady(true);

      tvWidgetRef.current?.applyOverrides({
        "paneProperties.topMargin": 10,
        "paneProperties.bottomMargin": 20,
      });

      const priceScale = tvWidgetRef.current?.activeChart().getPanes()[0]?.getMainSourcePriceScale();
      if (priceScale && !priceScale.isAutoScale()) {
        priceScale.setAutoScale(true);
      }

      tvWidgetRef.current?.headerReady().then(() => {
        tvWidgetRef.current?.createButton({
          align: "right",
          useTradingViewStyle: true,
          text: t`Copy Image`,
          title: t`Copy chart image`,
          onClick: async () => {
            try {
              const copyMode = await copyChartSnapshot();
              if (copyMode === "image") {
                helperToast.success(t`Image copied to clipboard.`);
              } else {
                helperToast.success(t`Image link copied to clipboard.`);
              }
            } catch (_error) {
              helperToast.error(t`Failed to copy image.`);
            }
          },
        });

        tvWidgetRef.current?.createButton({
          align: "right",
          useTradingViewStyle: true,
          text: t`Download`,
          title: t`Download chart image`,
          onClick: async () => {
            try {
              await downloadChartSnapshot();
              helperToast.success(t`Chart image downloaded.`);
            } catch (_error) {
              helperToast.error(t`Failed to download chart image.`);
            }
          },
        });
      });

      const savedPeriod = tvWidgetRef.current?.activeChart().resolution();
      const preferredPeriod = getObjectKeyFromValue(period, supportedResolutions) as ResolutionString;

      if (savedPeriod && savedPeriod !== preferredPeriod) {
        tvWidgetRef.current?.activeChart().setResolution(preferredPeriod);
      }

      tvWidgetRef.current
        ?.activeChart()
        .onIntervalChanged()
        .subscribe(null, (interval) => {
          if (supportedResolutions[interval]) {
            setChartDataLoading(true);
            const period = supportedResolutions[interval];
            setPeriod(period);
            const activeChart = tvWidgetRef.current?.activeChart();
            resetChartData(activeChart, datafeed, symbolRef.current, interval);
            tvWidgetRef.current?.saveChartToServer(undefined, undefined, {
              chartName: `gmx-chart-v${tradePageVersion}`,
            });

            const priceScale = activeChart?.getPanes().at(0)?.getMainSourcePriceScale();
            if (priceScale) {
              priceScale.setAutoScale(true);
            }
          }
        });

      tvWidgetRef.current
        ?.activeChart()
        .onDataLoaded()
        .subscribe(null, () => {
          setChartDataLoading(false);
        });

      tvWidgetRef.current?.subscribe("onMarkClick", (markId) => {
        const details = datafeed.getMarkDetails(markId);
        if (!details) {
          return;
        }

        setSelectedTradeMark(details);
      });

      tvWidgetRef.current?.subscribe("onAutoSaveNeeded", () => {
        tvWidgetRef.current?.saveChartToServer(undefined, undefined, {
          chartName: `gmx-chart-v${tradePageVersion}`,
        });
      });

      tvWidgetRef.current?.activeChart().dataReady(() => {
        setChartDataLoading(false);
      });
    });

    return () => {
      if (tvWidgetRef.current) {
        tvWidgetRef.current.remove();
        tvWidgetRef.current = null;
        setChartReady(false);
        setChartDataLoading(true);
      }
    };
    // We don't want to re-initialize the chart when the symbol changes. This will make the chart flicker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId, chartLocale, datafeed]);

  const style = useMemo<CSSProperties>(
    () => ({ visibility: !chartDataLoading ? "visible" : "hidden" }),
    [chartDataLoading]
  );
  const shouldShowStaticChartLines = savedShouldShowPositionLines ?? shouldShowPositionLines;
  const staticLinesKey = `${chartToken.symbol}:${marketAddress ?? ""}:${period}`;

  return (
    <div className="ExchangeChart-error">
      {chartDataLoading && <Loader />}
      <div style={style} ref={chartContainerRef} className="ExchangeChart-bottom-content" />
      {chartReady && !chartDataLoading && !isChartChangingSymbol && shouldShowStaticChartLines && (
        <StaticLines key={staticLinesKey} tvWidgetRef={tvWidgetRef} chartLines={chartLines} />
      )}
      {chartReady &&
        !chartDataLoading &&
        !isChartChangingSymbol &&
        showDynamicChartLines &&
        shouldShowPositionLines &&
        tradePageVersion === 2 && <DynamicLines isMobile={isMobile} tvWidgetRef={tvWidgetRef} />}
      <Modal
        isVisible={Boolean(selectedTradeMark)}
        setIsVisible={(isVisible) => {
          if (!isVisible) {
            setSelectedTradeMark(undefined);
          }
        }}
        label={selectedTradeMark?.source === "open-position" ? t`Open position` : t`Trade execution`}
        contentClassName="!w-[min(94vw,420px)] !max-w-[420px]"
      >
        <div className="flex flex-col gap-12 text-14">
          <div className="flex items-center justify-between gap-12">
            <span className="text-gray-lightest">{t`Side`}</span>
            <span className="font-medium">{selectedTradeMark?.side === "B" ? t`Buy` : t`Sell`}</span>
          </div>
          <div className="flex items-center justify-between gap-12">
            <span className="text-gray-lightest">{t`Order type`}</span>
            <span className="font-medium">{getOrderTypeLabel(getTradeMarkOrderType(selectedTradeMark))}</span>
          </div>
          {getTradeMarkExecutionType(selectedTradeMark) !== undefined && (
            <div className="flex items-center justify-between gap-12">
              <span className="text-gray-lightest">{t`Execution type`}</span>
              <span className="font-medium">{getExecutionTypeLabel(getTradeMarkExecutionType(selectedTradeMark))}</span>
            </div>
          )}
          <div className="flex items-center justify-between gap-12">
            <span className="text-gray-lightest">{getTradeMarkPriceLabel(selectedTradeMark)}</span>
            <span className="font-medium">{selectedTradeMarkPrice}</span>
          </div>
          <div className="flex items-center justify-between gap-12">
            <span className="text-gray-lightest">
              {selectedTradeMark?.source === "open-position" ? t`Position size` : t`Size`}
            </span>
            <span className="font-medium">{formatUsd(toBigIntOrUndefined(selectedTradeMark?.sizeDeltaUsd))}</span>
          </div>
          <div className="flex items-center justify-between gap-12">
            <span className="text-gray-lightest">{t`Time`}</span>
            <span className="font-medium">
              {selectedTradeMark?.time ? formatDateTime(selectedTradeMark.time) : "-"}
            </span>
          </div>
          <div className="flex items-center justify-between gap-12">
            <span className="text-gray-lightest">{t`Transaction`}</span>
            <span className="font-medium">
              {selectedTradeMark?.txHash ? (
                <ExternalLink href={`${explorerUrl}tx/${selectedTradeMark.txHash}`}>
                  {shortenAddress(selectedTradeMark.txHash, 6)}
                </ExternalLink>
              ) : (
                "-"
              )}
            </span>
          </div>
        </div>
      </Modal>
    </div>
  );
}
